import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, take, tap } from 'rxjs/operators';
import { pickTruthy } from 'Util';
import { Location } from '../../models/Location';
import { Pinnable } from '../../models/Pinnable';
import { HallPassesService, ROOM_DELETION_ERROR } from '../../services/hall-passes.service';
import { LocationsService } from '../../services/locations.service';
import { ToastService } from '../../services/toast.service';
import { receiveUpdate } from '../live-updates/live-updates.actions';
import * as pinnablesActions from '../pinnables/actions';
import * as locationsActions from './locations.actions';

@Injectable()
export class LocationsEffects {
	getLocations$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.getLocations),
			concatMap((action) =>
				this.locService.getLocationsWithConfig(action.url).pipe(
					map((locations) => locationsActions.getLocationsSuccess({ locations: locations.results })),
					catchError((error) => of(locationsActions.getLocationsFailure({ errorMessage: error.message })))
				)
			)
		)
	);

	getLocationsFromCategory$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.getLocationsFromCategory),
			concatMap((action) =>
				this.locService.getLocationsWithCategory(action.category).pipe(
					map((locations) => locationsActions.getLocationsFromCategorySuccess({ locations })),
					catchError((error) => of(locationsActions.getLocationsFromCategoryFailure({ errorMessage: error.message })))
				)
			)
		)
	);

	searchLocations$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.searchLocations),
			concatMap((action) =>
				this.locService.getLocationsWithConfig(action.url).pipe(
					map((foundLocations) => locationsActions.searchLocationsSuccess({ foundLocations: foundLocations.results })),
					catchError((error) => of(locationsActions.searchLocationsFailure({ errorMessage: error.message })))
				)
			)
		)
	);

	postLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.postLocation),
			concatMap((action) =>
				this.locService.createLocation(action.data).pipe(
					switchMap((location) => this.locService.updateLocation(location.id, action.data)),
					map((location) => locationsActions.postLocationSuccess({ location })),
					catchError((error) => of(locationsActions.postLocationFailure({ errorMessage: error.message })))
				)
			)
		)
	);

	updateLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.updateLocation),
			concatMap((action) =>
				this.locService.updateLocation(action.id, action.data).pipe(
					map((location) => locationsActions.updateLocationSuccess({ location })),
					catchError((error) => of(locationsActions.updateLocationFailure({ errorMessage: error.message })))
				)
			)
		)
	);

	updateLocationSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.updateLocationSuccess),
			exhaustMap((action) =>
				this.passesService.pinnables$.pipe(
					take(1),
					map((pinnables) => pinnables.filter((p) => p.location).find((p) => p.location.id === action.location.id)),
					pickTruthy(),
					map((pinnable: Pinnable) => {
						const updatedPinnable = {
							...pinnable,
							location: action.location,
						};
						return pinnablesActions.updatePinnableSuccess({ pinnable: updatedPinnable as Pinnable });
					})
				)
			)
		)
	);

	removeLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.removeLocation),
			concatMap((action) =>
				this.locService.deleteLocation(action.id).pipe(
					map(() => locationsActions.removeLocationSuccess({ id: action.id })),
					catchError((error) => of(locationsActions.removeLocationFailure({ error: error })))
				)
			)
		)
	);

	removeLocationFailure$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(locationsActions.removeLocationFailure),
				tap((error) => {
					if (error.error?.error?.detail?.includes(ROOM_DELETION_ERROR)) {
						this.toast.openToast({
							title: 'Oh no! Something went wrong',
							subtitle: error.error.error.detail.split(ROOM_DELETION_ERROR)[1],
							type: 'error',
						});
					}
				})
			),
		{ dispatch: false }
	);

	getLocationsWithPinnables$ = createEffect(() =>
		this.actions$.pipe(
			ofType(locationsActions.fetchLocationsWithPinnables),
			concatMap(() =>
				this.locService.getLocationsWithPinnables().pipe(
					map((locations) => locationsActions.fetchLocationsWithPinnablesSuccess({ locations })),
					catchError((error) =>
						of(
							locationsActions.fetchLocationsWithPinnablesFailure({
								errorMessage: error.message,
							})
						)
					)
				)
			)
		)
	);

	receiveUpdate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(receiveUpdate),
			map((action) => {
				if (action.event.action === 'location.patched') {
					const location = Location.fromJSON(action.event.data);
					return location && locationsActions.receiveLiveLocationUpdate({ data: location });
				} else if (action.event.action === 'pinnable.patched') {
					const pinnable = Pinnable.fromJSON(action.event.data);
					return pinnable && locationsActions.receiveLivePinnableUpdate({ data: pinnable });
				} else {
					return null;
				}
			}),
			pickTruthy()
		)
	);

	constructor(
		private actions$: Actions,
		private locService: LocationsService,
		private passesService: HallPassesService,
		private toast: ToastService
	) {}
}
